import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { ALERT_TONE_CLASSES } from '../ui/Alert';

// Maintenance banner. The flag lives on clients.utilsync.cloud: one KV key turns
// the banner on for both sites (workers/client-portal/src/index.ts,
// getMaintenance). main.tsx mounts this OUTSIDE AuthProvider, so it shows on the
// realm picker and while Keycloak is down, which is when it matters most.
//
// Every failure is quiet: a status check must never block or break the app.
export const MAINTENANCE_STATUS_URL = 'https://clients.utilsync.cloud/api/maintenance';
// Pages stay open for hours; re-check so a toggle reaches them without a reload.
export const MAINTENANCE_POLL_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 5000;

export default function MaintenanceBanner() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        const res = await fetch(MAINTENANCE_STATUS_URL, { credentials: 'omit', signal: ctrl.signal });
        const data = (res.ok ? await res.json() : null) as { enabled?: unknown; message?: unknown } | null;
        if (cancelled) return;
        setMessage(
          data && data.enabled === true && typeof data.message === 'string' && data.message !== ''
            ? data.message
            : null,
        );
      } catch {
        // Network error, timeout or bad JSON: keep whatever we showed last.
      } finally {
        clearTimeout(timer);
      }
    };
    void check();
    const id = setInterval(() => void check(), MAINTENANCE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'flex items-center justify-center gap-2 border-b px-4 py-2 text-center text-sm font-medium',
        ALERT_TONE_CLASSES.warning,
      )}
    >
      <svg aria-hidden="true" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      {/* min-w-0 on the flex item itself, so a long message wraps instead of
          setting a min-content floor wider than a 375px screen. */}
      <span className="min-w-0">{message}</span>
    </div>
  );
}
